import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PHONE_DISPLAY, EMAIL, SERVICE_AREA } from "@/lib/brand";
import "./BudgetPlanner.css";

// ---------------------------------------------------------------------------
// Fallback benchmark ages used when a homeowner leaves a system age blank.
// Unknown ages never break the calculator — they only lower confidence.
// ---------------------------------------------------------------------------
const FALLBACK = { roof: 10, hvac: 8, waterHeater: 5, appliance: 5 };

const STORY_MULTIPLIER = { "1": 1.0, "1.5": 1.03, "2": 1.07, "3": 1.12 };

const FEATURE_BURDEN = {
  deck: 90,
  basement: 60,
  garage: 60,
  landscaping: 180,
  pool: 420,
  hvacs: 90,
  crawlspace: 60,
  well: 60,
  septic: 80,
  trees: 120,
};

const FEATURE_LABELS = [
  ["deck", "Deck / Outdoor Living Area"],
  ["basement", "Basement"],
  ["garage", "Garage"],
  ["landscaping", "Significant Landscaping"],
  ["pool", "Swimming Pool"],
  ["hvacs", "Multiple HVAC Systems"],
  ["crawlspace", "Crawlspace"],
  ["well", "Private Well"],
  ["septic", "Septic System"],
  ["trees", "Large / Mature Trees"],
];

const usd = (n) =>
  "$" + Math.round(n).toLocaleString("en-US", { maximumFractionDigits: 0 });

const roundTo = (n, step) => Math.round(n / step) * step;

const parseAge = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  if (Number.isNaN(n) || n < 0) return null;
  return n;
};

// ---------------------------------------------------------------------------
// Core calculation model (faithful to the Heavenly spec)
// ---------------------------------------------------------------------------
function computePlan(input) {
  const sqft = Number(input.sqft);
  const value = input.value === "" ? null : Number(input.value);
  const { age, condition, exterior, features } = input;
  const stories = input.stories;

  // ---- LAYER 1 — Routine maintenance burden -----------------------------
  let routineAnnual = sqft * 0.75;
  if (age === "old") routineAnnual += sqft * 0.1;
  if (age === "historic") routineAnnual += sqft * 0.2;
  if (condition === "fair") routineAnnual += sqft * 0.15;
  routineAnnual *= STORY_MULTIPLIER[stories] ?? 1.0;
  if (exterior === "wood") routineAnnual += 180;
  if (exterior === "stucco") routineAnnual += 90;
  Object.keys(FEATURE_BURDEN).forEach((k) => {
    if (features[k]) routineAnnual += FEATURE_BURDEN[k];
  });
  let routineMonthly = routineAnnual / 12;

  // ---- LAYER 2 — Replacement reserve ------------------------------------
  const baselineReplacementAnnual = sqft * 0.55;
  let replacementMonthly = baselineReplacementAnnual / 12;

  let exposureMin = 0;
  let exposureMax = 0;
  const priorities = [];
  let highPriorityCount = 0;
  let startPlanningCount = 0;

  const rawRoof = parseAge(input.roofAge);
  const rawHvac = parseAge(input.hvacAge);
  const rawWater = parseAge(input.waterHeaterAge);
  const rawAppliance = parseAge(input.applianceAge);

  // ROOF ------------------------------------------------------------------
  const footprintRoofSizing = sqft * 1.25;
  const replacementCostRoof = footprintRoofSizing * 4.8;
  const roofAge = rawRoof ?? FALLBACK.roof;
  if (rawRoof === null) {
    priorities.push({
      cls: "",
      dot: "●",
      title: "Roof — Age Unknown",
      msg: `We used a benchmark age of ${FALLBACK.roof} years to keep your estimate moving. A quick physical inspection would sharpen this considerably.`,
    });
  } else if (roofAge >= 20) {
    replacementMonthly += 55;
    exposureMin += replacementCostRoof * 0.9;
    exposureMax += replacementCostRoof * 1.1;
    highPriorityCount += 1;
    priorities.push({
      cls: "danger",
      dot: "🔴",
      title: "Roof — High Priority",
      msg: "Your roof is at or beyond typical service life. Prioritize building a dedicated reserve and consider an inspection soon.",
    });
  } else if (roofAge >= 14) {
    replacementMonthly += 25;
    exposureMin += replacementCostRoof * 0.8;
    exposureMax += replacementCostRoof * 1.0;
    startPlanningCount += 1;
    priorities.push({
      cls: "warning",
      dot: "🟠",
      title: "Roof — Start Planning",
      msg: "Your roof is entering its later years. Start setting funds aside now so a future replacement isn't a surprise.",
    });
  } else {
    priorities.push({
      cls: "",
      dot: "🟢",
      title: "Roof — Routine Priority",
      msg: "Your roof is well within its expected service life. Routine upkeep and periodic checks are all that's needed for now.",
    });
  }

  // HVAC ------------------------------------------------------------------
  const replacementCostHVAC = features.hvacs ? 11500 : 6500;
  const hvacAge = rawHvac ?? FALLBACK.hvac;
  if (rawHvac === null) {
    priorities.push({
      cls: "",
      dot: "●",
      title: "HVAC — Age Unknown",
      msg: `We applied a benchmark age of ${FALLBACK.hvac} years. Knowing the real age (often on the unit's data plate) improves accuracy.`,
    });
  } else if (hvacAge >= 15) {
    replacementMonthly += 48;
    exposureMin += replacementCostHVAC * 0.9;
    exposureMax += replacementCostHVAC * 1.1;
    highPriorityCount += 1;
    priorities.push({
      cls: "danger",
      dot: "🔴",
      title: "HVAC — High Priority",
      msg: "Your heating/cooling system is past the typical replacement window. A dedicated reserve is strongly recommended.",
    });
  } else if (hvacAge >= 10) {
    replacementMonthly += 24;
    exposureMin += replacementCostHVAC * 0.8;
    exposureMax += replacementCostHVAC * 1.0;
    startPlanningCount += 1;
    priorities.push({
      cls: "warning",
      dot: "🟠",
      title: "HVAC — Start Planning",
      msg: "Your HVAC is entering its higher-maintenance years. Begin reserving now to soften the cost of an eventual replacement.",
    });
  } else {
    priorities.push({
      cls: "",
      dot: "🟢",
      title: "HVAC — Routine Priority",
      msg: "Your HVAC is within its expected service life. Keep up with filter changes and seasonal servicing.",
    });
  }

  // WATER HEATER ----------------------------------------------------------
  const replacementCostWaterHeater = 1750;
  const waterAge = rawWater ?? FALLBACK.waterHeater;
  if (rawWater === null) {
    priorities.push({
      cls: "",
      dot: "●",
      title: "Water Heater — Age Unknown",
      msg: `We used a benchmark age of ${FALLBACK.waterHeater} years. Water heaters often have a manufacture date on the label.`,
    });
  } else if (waterAge >= 10) {
    replacementMonthly += 18;
    exposureMin += replacementCostWaterHeater * 0.9;
    exposureMax += replacementCostWaterHeater * 1.1;
    highPriorityCount += 1;
    priorities.push({
      cls: "danger",
      dot: "🔴",
      title: "Water Heater — High Priority",
      msg: "Your water heater is beyond the typical service life. Proactive replacement helps avoid leaks and water damage.",
    });
  } else if (waterAge >= 7) {
    replacementMonthly += 10;
    exposureMin += replacementCostWaterHeater * 0.8;
    exposureMax += replacementCostWaterHeater * 1.0;
    startPlanningCount += 1;
    priorities.push({
      cls: "warning",
      dot: "🟠",
      title: "Water Heater — Start Planning",
      msg: "Your water heater is approaching end-of-life. Start budgeting so you can replace it on your terms.",
    });
  } else {
    priorities.push({
      cls: "",
      dot: "🟢",
      title: "Water Heater — Routine Priority",
      msg: "Your water heater is within its expected service life. An annual flush helps it last.",
    });
  }

  // APPLIANCES ------------------------------------------------------------
  const applianceAge = rawAppliance ?? FALLBACK.appliance;
  if (rawAppliance === null) {
    // no premium; note in priorities lightly
  } else if (applianceAge >= 10) {
    replacementMonthly += 14;
    startPlanningCount += 1;
    priorities.push({
      cls: "warning",
      dot: "🟠",
      title: "Appliances — Start Planning",
      msg: "Your major appliances are past their typical lifespan. Expect one or more to need replacing before long.",
    });
  } else if (applianceAge >= 7) {
    replacementMonthly += 7;
    priorities.push({
      cls: "",
      dot: "🟢",
      title: "Appliances — Keep an Eye Out",
      msg: "Your major appliances are getting older. A small monthly set-aside keeps a future replacement comfortable.",
    });
  }

  // ---- Combine + value modifier -----------------------------------------
  let valueNote = null;
  if (value && value > 0 && sqft > 0) {
    const priceDensityIndex = value / sqft;
    if (priceDensityIndex > 230) {
      routineMonthly *= 1.12;
      replacementMonthly *= 1.12;
      exposureMin *= 1.15;
      exposureMax *= 1.15;
      valueNote =
        "Your home's value-per-square-foot is above average, so finishes and materials tend to cost more to maintain and replace — we increased your reserve by 12% and potential exposure by 15%.";
    } else if (priceDensityIndex < 90) {
      routineMonthly *= 0.92;
      replacementMonthly *= 0.92;
      exposureMin *= 0.85;
      exposureMax *= 0.85;
      valueNote =
        "Your home's value-per-square-foot is on the modest side, so we reduced your reserve by 8% and potential exposure by 15%.";
    }
  }

  const totalMonthly = routineMonthly + replacementMonthly;

  // ---- Confidence -------------------------------------------------------
  const knownAges = [rawRoof, rawHvac, rawWater, rawAppliance].filter(
    (v) => v !== null
  ).length;
  let confScore = knownAges + (value && value > 0 ? 1 : 0);
  let confidence;
  if (confScore >= 4) confidence = "Excellent";
  else if (confScore === 3) confidence = "Good";
  else if (confScore === 2) confidence = "Moderate";
  else confidence = "Limited";

  // ---- Profile ----------------------------------------------------------
  const maintenanceLevel =
    age === "old" || age === "historic" || condition === "fair"
      ? "Elevated"
      : "Stable";

  let replacementRisk = "Stable";
  if (highPriorityCount > 0) replacementRisk = "High Priority";
  else if (startPlanningCount > 0) replacementRisk = "Elevated";

  // ---- Narrative --------------------------------------------------------
  let narrative;
  if (highPriorityCount > 0) {
    narrative =
      "Your maintenance profile is currently high priority. Several major systems are approaching or exceeding typical service-life ranges, meaning multiple large expenses could potentially occur within the same period. Building your reserve now — and addressing the oldest systems first — can substantially reduce the risk of being caught by surprise.";
  } else if (startPlanningCount > 0) {
    narrative =
      "Your recommended maintenance reserve is elevated because your major systems are entering higher-maintenance or replacement windows. Building your reserve now can help prevent a major repair from becoming a financial emergency.";
  } else {
    narrative =
      "Your current maintenance profile is relatively stable. Most major systems are still within their expected service life, so your recommended monthly reserve is primarily intended to build a cushion for routine maintenance and future replacements.";
  }

  // ---- Rationale factors ------------------------------------------------
  const factors = [];
  factors.push(
    `Routine baseline: ${usd(sqft * 0.75)}/yr (${sqft.toLocaleString()} sq ft × $0.75).`
  );
  if (age === "old") factors.push("Home age (16–30 yrs) added a routine premium.");
  if (age === "historic")
    factors.push("Historic home age (31+ yrs) added a larger routine premium.");
  if (condition === "fair")
    factors.push("Fair condition added a routine upkeep premium.");
  if ((STORY_MULTIPLIER[stories] ?? 1) !== 1)
    factors.push(
      `${stories}-story adjustment applied a ${Math.round(
        (STORY_MULTIPLIER[stories] - 1) * 100
      )}% routine multiplier.`
    );
  factors.push(
    `Replacement baseline sinking fund: ${usd(baselineReplacementAnnual)}/yr (${sqft.toLocaleString()} sq ft × $0.55).`
  );
  if (highPriorityCount > 0 || startPlanningCount > 0)
    factors.push(
      "Aging major systems added monthly 'pressure premiums' to your replacement reserve."
    );
  if (valueNote) factors.push(valueNote);
  factors.push(
    "Unknown system ages were estimated with conservative benchmarks and lowered your Estimate Confidence rather than blocking the result."
  );

  return {
    routineMonthly: Math.round(routineMonthly),
    replacementMonthly: Math.round(replacementMonthly),
    totalMonthly: Math.round(totalMonthly),
    exposureMin: exposureMax > 0 ? roundTo(exposureMin, 50) : 0,
    exposureMax: roundTo(exposureMax, 50),
    hasExposure: exposureMax > 0,
    isHighRisk: highPriorityCount > 0,
    maintenanceLevel,
    replacementRisk,
    confidence,
    narrative,
    factors,
    priorities,
  };
}

function buildPlanText(input, r) {
  const lines = [];
  lines.push("HEAVENLY HOME & OFFICE");
  lines.push("Personalized Home Maintenance Plan");
  lines.push("Generated: " + new Date().toLocaleDateString());
  lines.push("=".repeat(48));
  lines.push("");
  lines.push("PROPERTY PROFILE");
  lines.push(`  Home Size: ${input.sqft} sq ft`);
  if (input.value) lines.push(`  Estimated Value: $${Number(input.value).toLocaleString()}`);
  lines.push(`  Home Age: ${input.age}`);
  lines.push(`  Stories: ${input.stories}`);
  lines.push(`  Condition: ${input.condition}`);
  lines.push(`  Exterior: ${input.exterior}`);
  lines.push("");
  lines.push("RECOMMENDED MONTHLY RESERVE");
  lines.push(`  Routine Maintenance Reserve: ${usd(r.routineMonthly)} / month`);
  lines.push(`  Replacement Reserve:         ${usd(r.replacementMonthly)} / month`);
  lines.push(`  TOTAL HOME RESERVE:          ${usd(r.totalMonthly)} / month`);
  lines.push("");
  if (r.hasExposure) {
    lines.push("POTENTIAL UPCOMING MAJOR-REPAIR COSTS");
    lines.push(`  ${usd(r.exposureMin)} – ${usd(r.exposureMax)}`);
    lines.push("");
  }
  lines.push("PROFILE");
  lines.push(`  Maintenance Level:   ${r.maintenanceLevel}`);
  lines.push(`  Replacement Risk:    ${r.replacementRisk}`);
  lines.push(`  Estimate Confidence: ${r.confidence}`);
  lines.push("");
  lines.push("HIGHEST-PRIORITY SYSTEMS");
  r.priorities.forEach((p) => {
    lines.push(`  • ${p.title}`);
    lines.push(`      ${p.msg}`);
  });
  lines.push("");
  lines.push("HOW YOUR RESERVE WAS CALCULATED");
  r.factors.forEach((f) => lines.push(`  • ${f}`));
  lines.push("");
  lines.push("ASSESSMENT");
  lines.push("  " + r.narrative);
  lines.push("");
  lines.push("-".repeat(48));
  lines.push(
    "Planning estimates only. Actual maintenance and replacement costs vary by location, materials, system type, labor rates, property condition, access, and market conditions. This tool does not constitute a contractor quote, inspection, warranty, or guarantee that a repair or replacement will be required."
  );
  lines.push("");
  lines.push(`Ready to act on your plan? Call Heavenly Home & Office: ${PHONE_DISPLAY}`);
  return lines.join("\n");
}

const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const AGE_LABELS = { new: "0–5 years", mid: "6–15 years", old: "16–30 years", historic: "31+ years" };
const COND_LABELS = { excellent: "Excellent / Pristine", good: "Good / Normal Wear", fair: "Fair / Minor Issues" };
const EXT_LABELS = { vinyl: "Vinyl Siding", brick: "Brick / Masonry", wood: "Wood Siding", stucco: "Stucco / Composite" };

// Branded, print-ready document (customer can Save as PDF from the print dialog)
function buildPlanHtml(input, r, lead) {
  const dot = (cls) =>
    cls === "danger" ? "#dc2626" : cls === "warning" ? "#ea580c" : "#16a34a";
  const prioRows = r.priorities
    .map(
      (p) => `<tr>
        <td style="width:10px;vertical-align:top;padding:6px 8px;"><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${dot(p.cls)};"></span></td>
        <td style="padding:6px 8px;"><strong>${esc(p.title)}</strong><br/><span style="color:#475569;font-size:12px;">${esc(p.msg)}</span></td>
      </tr>`
    )
    .join("");
  const factorRows = r.factors.map((f) => `<li>${esc(f)}</li>`).join("");
  return `<!doctype html><html><head><meta charset="utf-8"/>
  <title>Heavenly Home Maintenance Plan</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; color:#1e293b; margin:0; padding:32px; }
    .doc { max-width:720px; margin:0 auto; }
    .head { border-bottom:3px solid #0284c7; padding-bottom:12px; margin-bottom:20px; }
    .brand { font-size:22px; font-weight:800; color:#0284c7; margin:0; }
    .sub { color:#64748b; font-size:13px; margin:4px 0 0; }
    h2 { font-size:14px; text-transform:uppercase; letter-spacing:.05em; color:#64748b; border-bottom:1px solid #e2e8f0; padding-bottom:6px; margin:24px 0 12px; }
    .grid { display:grid; grid-template-columns:1fr 1fr; gap:6px 24px; font-size:13px; }
    .row { display:flex; justify-content:space-between; padding:8px 0; border-bottom:1px solid #f1f5f9; font-size:14px; }
    .total { font-weight:800; color:#166534; font-size:17px; }
    .badges { display:flex; gap:10px; margin:8px 0; }
    .badge { flex:1; border:1px solid #e2e8f0; border-radius:8px; padding:10px; text-align:center; font-size:11px; color:#64748b; }
    .badge b { display:block; font-size:15px; color:#1e293b; margin-top:2px; }
    .exposure { background:#fff7ed; border:1px solid #fed7aa; border-radius:10px; padding:14px; text-align:center; margin:12px 0; }
    .exposure .big { font-size:26px; font-weight:800; color:#c2410c; }
    .disc { font-size:11px; color:#64748b; line-height:1.5; margin-top:20px; border-top:1px solid #e2e8f0; padding-top:12px; }
    ul { padding-left:18px; font-size:12.5px; color:#475569; }
    @media print { body { padding:0; } }
  </style></head>
  <body><div class="doc">
    <div class="head">
      <p class="brand">Heavenly Home &amp; Office</p>
      <p class="sub">Personalized Home Maintenance Plan &nbsp;•&nbsp; ${esc(new Date().toLocaleDateString())}</p>
      <p class="sub">Prepared for: <strong>${esc(lead.name)}</strong>${lead.phone ? " &nbsp;•&nbsp; " + esc(lead.phone) : ""}${lead.email ? " &nbsp;•&nbsp; " + esc(lead.email) : ""}${lead.area ? "<br/>Property: " + esc(lead.area) : ""}</p>
    </div>

    <h2>Property Profile</h2>
    <div class="grid">
      <div>Home Size: <strong>${esc(input.sqft)} sq ft</strong></div>
      ${input.value ? `<div>Estimated Value: <strong>$${Number(input.value).toLocaleString()}</strong></div>` : "<div></div>"}
      <div>Home Age: <strong>${esc(AGE_LABELS[input.age])}</strong></div>
      <div>Stories: <strong>${esc(input.stories)}</strong></div>
      <div>Condition: <strong>${esc(COND_LABELS[input.condition])}</strong></div>
      <div>Exterior: <strong>${esc(EXT_LABELS[input.exterior])}</strong></div>
    </div>

    <h2>Your Maintenance Profile</h2>
    <div class="badges">
      <div class="badge">Maintenance Level<b>${esc(r.maintenanceLevel)}</b></div>
      <div class="badge">Replacement Risk<b>${esc(r.replacementRisk)}</b></div>
      <div class="badge">Estimate Confidence<b>${esc(r.confidence)}</b></div>
    </div>

    <h2>Recommended Monthly Reserve</h2>
    <div class="row"><span>Routine Maintenance Reserve</span><span>${usd(r.routineMonthly)}/mo</span></div>
    <div class="row"><span>Replacement Reserve</span><span>${usd(r.replacementMonthly)}/mo</span></div>
    <div class="row total"><span>Recommended Total Home Reserve</span><span>${usd(r.totalMonthly)}/mo</span></div>

    ${
      r.hasExposure
        ? `<h2>Potential Upcoming Major-Repair Costs</h2>
    <div class="exposure"><div class="big">${usd(r.exposureMin)} – ${usd(r.exposureMax)}</div>
    <div style="font-size:12px;color:#475569;margin-top:4px;">Based on the age of your major systems. Not a contractor quote or guarantee.</div></div>`
        : ""
    }

    <h2>Assessment</h2>
    <p style="font-size:13px;color:#334155;line-height:1.5;">${esc(r.narrative)}</p>

    <h2>Highest-Priority Systems</h2>
    <table style="width:100%;border-collapse:collapse;">${prioRows}</table>

    <h2>How Your Reserve Was Calculated</h2>
    <ul>${factorRows}</ul>

    <p class="disc">Planning estimates only. Actual maintenance and replacement costs vary by location, materials, system type, labor rates, property condition, access, and market conditions. This tool does not constitute a contractor quote, inspection, warranty, or guarantee that a repair or replacement will be required. For a personalized quote, Heavenly Home &amp; Office will review the specific job(s) you need done.</p>
    <p class="disc" style="border:none;padding-top:0;">Questions? Call ${esc(PHONE_DISPLAY)} &nbsp;•&nbsp; ${esc(EMAIL)} &nbsp;•&nbsp; ${esc(SERVICE_AREA)}</p>
  </div></body></html>`;
}

// Render the printable doc in a hidden iframe and open the print dialog
// (works around popup blockers; the user can "Save as PDF").
function printPlanDocument(html) {
  const existing = document.getElementById("hho-print-frame");
  if (existing) existing.remove();
  const iframe = document.createElement("iframe");
  iframe.id = "hho-print-frame";
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }, 250);
  };
}


const blankInput = {
  sqft: "",
  value: "",
  age: "mid",
  stories: "1",
  condition: "good",
  exterior: "vinyl",
  roofAge: "",
  hvacAge: "",
  waterHeaterAge: "",
  applianceAge: "",
  features: {
    deck: false,
    basement: false,
    garage: false,
    landscaping: false,
    pool: false,
    hvacs: false,
    crawlspace: false,
    well: false,
    septic: false,
    trees: false,
  },
};

export default function BudgetPlanner() {
  const [input, setInput] = useState(blankInput);
  const [result, setResult] = useState(null);
  const [sqftError, setSqftError] = useState("");
  const [accordionOpen, setAccordionOpen] = useState(false);

  const [lead, setLead] = useState({ name: "", phone: "", email: "", area: "" });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const usagePingSent = useRef(false);

  const setField = (k) => (e) =>
    setInput((s) => ({ ...s, [k]: e.target.value }));

  const toggleFeature = (k) => (e) =>
    setInput((s) => ({ ...s, features: { ...s.features, [k]: e.target.checked } }));

  const handleCalculate = () => {
    const sqft = Number(input.sqft);
    if (!input.sqft || Number.isNaN(sqft) || sqft <= 0) {
      setSqftError("Please enter a valid home size (square footage) to continue.");
      setResult(null);
      return;
    }
    setSqftError("");
    const r = computePlan(input);
    setResult(r);
    setLeadDone(false);

    // Analytics ping — fire ONCE per visit so recalculations don't flood the inbox
    if (!usagePingSent.current) {
      usagePingSent.current = true;
      api
        .post("/calculator-event", {
          action: "calculated",
          summary: buildPlanText(input, r),
        })
        .catch(() => {
          /* analytics is best-effort; never blocks the user */
        });
    }

    // Reveal + scroll results into view
    setTimeout(() => {
      const el = document.getElementById("reserve-results");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (leadSubmitting || !result) return;
    if (!lead.name.trim() || !lead.phone.trim() || !lead.email.trim()) {
      toast.error("Please add your name, phone, and email to get your printable plan.");
      return;
    }

    setLeadSubmitting(true);
    const planText = buildPlanText(input, result);

    // 1) Instantly deliver a branded, printable plan (customer can Save as PDF)
    try {
      printPlanDocument(buildPlanHtml(input, result, lead));
    } catch (err) {
      /* print is best-effort */
    }

    // 2) Notify the owner (real Resend-backed lead) so Heavenly can follow up
    try {
      await api.post("/maintenance-plan", {
        name: lead.name,
        phone: lead.phone,
        email: lead.email,
        addressOrArea: lead.area,
        summary: planText,
      });
      setLeadDone(true);
    } catch (err) {
      // The plan still printed for the user; only the owner notification failed.
      setLeadDone(true);
      toast.message("Your printable plan is ready. We'll still follow up shortly.");
    } finally {
      setLeadSubmitting(false);
    }
  };

  const scrollToQuote = () => {
    const el = document.getElementById("quote");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="budget-planner"
      className="py-24 md:py-32 bg-white"
      data-testid="budget-planner-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="text-xs font-semibold uppercase tracking-widest text-sky-700">
            Free Planning Tool
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-slate-900 tracking-tight">
            Home Maintenance Budget Planner
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Understand what you may need to budget for <em>before</em> a major home repair
            becomes a financial surprise.
          </p>
        </div>

        <div className="heavenly-calc-container" data-testid="budget-planner">
          <h3>🏠 Home Maintenance Budget Planner</h3>
          <p className="calc-subtitle">
            Estimate how much you should set aside each month for future home maintenance
            and major replacements.
          </p>

          {/* SECTION 1 — PROPERTY PROFILE */}
          <div className="section-title">Property Profile</div>
          <div className="grid-2">
            <div className="input-group">
              <label htmlFor="home-sqft">Home Size (sq ft)</label>
              <input
                id="home-sqft"
                type="number"
                min="0"
                inputMode="numeric"
                placeholder="e.g. 1800"
                value={input.sqft}
                onChange={setField("sqft")}
                className={sqftError ? "input-error" : ""}
                data-testid="bp-input-sqft"
              />
              {sqftError && (
                <span className="inline-error" data-testid="bp-sqft-error">
                  {sqftError}
                </span>
              )}
            </div>
            <div className="input-group">
              <label htmlFor="home-value">Estimated Home Value</label>
              <span className="helper-text">Optional — helps fine-tune your estimate</span>
              <input
                id="home-value"
                type="number"
                min="0"
                inputMode="numeric"
                placeholder="e.g. 350000"
                value={input.value}
                onChange={setField("value")}
                data-testid="bp-input-value"
              />
            </div>
          </div>

          <div className="grid-2">
            <div className="input-group">
              <label htmlFor="home-age">Home Age</label>
              <select id="home-age" value={input.age} onChange={setField("age")} data-testid="bp-select-age">
                <option value="new">0–5 years</option>
                <option value="mid">6–15 years</option>
                <option value="old">16–30 years</option>
                <option value="historic">31+ years</option>
              </select>
            </div>
            <div className="input-group">
              <label htmlFor="home-stories">Stories</label>
              <select id="home-stories" value={input.stories} onChange={setField("stories")} data-testid="bp-select-stories">
                <option value="1">1 story</option>
                <option value="1.5">1.5 stories</option>
                <option value="2">2 stories</option>
                <option value="3">3+ stories</option>
              </select>
            </div>
          </div>

          <div className="grid-2">
            <div className="input-group">
              <label htmlFor="home-condition">Overall Condition</label>
              <select id="home-condition" value={input.condition} onChange={setField("condition")} data-testid="bp-select-condition">
                <option value="excellent">Excellent / Pristine</option>
                <option value="good">Good / Normal Wear</option>
                <option value="fair">Fair / Minor Issues</option>
              </select>
            </div>
            <div className="input-group">
              <label htmlFor="exterior-type">Exterior</label>
              <select id="exterior-type" value={input.exterior} onChange={setField("exterior")} data-testid="bp-select-exterior">
                <option value="vinyl">Vinyl Siding</option>
                <option value="brick">Brick / Masonry</option>
                <option value="wood">Wood Siding</option>
                <option value="stucco">Stucco / Composite</option>
              </select>
            </div>
          </div>

          {/* SECTION 2 — MAJOR HOME SYSTEMS */}
          <div className="section-title">Major Home Systems</div>
          <p className="helper-text" style={{ marginTop: "-8px", marginBottom: "12px" }}>
            Years; leave blank if unknown
          </p>
          <div className="grid-2">
            <div className="input-group">
              <label htmlFor="roof-age">Roof Age</label>
              <input id="roof-age" type="number" min="0" placeholder="Years" value={input.roofAge} onChange={setField("roofAge")} data-testid="bp-input-roof" />
            </div>
            <div className="input-group">
              <label htmlFor="hvac-age">HVAC Age</label>
              <input id="hvac-age" type="number" min="0" placeholder="Years" value={input.hvacAge} onChange={setField("hvacAge")} data-testid="bp-input-hvac" />
            </div>
          </div>
          <div className="grid-2">
            <div className="input-group">
              <label htmlFor="water-heater-age">Water Heater Age</label>
              <input id="water-heater-age" type="number" min="0" placeholder="Years" value={input.waterHeaterAge} onChange={setField("waterHeaterAge")} data-testid="bp-input-water" />
            </div>
            <div className="input-group">
              <label htmlFor="appliance-age">Major Appliances Age</label>
              <input id="appliance-age" type="number" min="0" placeholder="Years" value={input.applianceAge} onChange={setField("applianceAge")} data-testid="bp-input-appliance" />
            </div>
          </div>

          {/* SECTION 3 — ADDITIONAL PROPERTY FEATURES */}
          <div className="section-title">Additional Property Features</div>
          <div className="checkbox-grid">
            {FEATURE_LABELS.map(([key, label]) => (
              <label className="checkbox-item" key={key} htmlFor={`feat-${key}`}>
                <input
                  type="checkbox"
                  id={`feat-${key}`}
                  checked={input.features[key]}
                  onChange={toggleFeature(key)}
                  data-testid={`bp-feat-${key}`}
                />
                {label}
              </label>
            ))}
          </div>

          <button
            type="button"
            className="calc-btn"
            onClick={handleCalculate}
            data-testid="bp-calculate-btn"
          >
            Calculate My Reserve Plan
          </button>

          {/* RESULTS */}
          {result && (
            <div className="reserve-results-box" id="reserve-results" data-testid="bp-results">
              <h3 style={{ marginTop: 8 }}>Your Heavenly Home Maintenance Profile</h3>

              <div className="profile-status-grid">
                <div className={`status-badge-card ${result.maintenanceLevel === "Elevated" ? "is-elevated" : ""}`}>
                  Maintenance Level
                  <span data-testid="bp-level-maint">{result.maintenanceLevel}</span>
                </div>
                <div className={`status-badge-card ${result.replacementRisk === "High Priority" ? "is-high" : result.replacementRisk === "Elevated" ? "is-elevated" : ""}`}>
                  Replacement Risk
                  <span data-testid="bp-level-risk">{result.replacementRisk}</span>
                </div>
                <div className="status-badge-card">
                  Estimate Confidence
                  <span data-testid="bp-level-confidence">{result.confidence}</span>
                </div>
              </div>

              {/* Financial breakdown */}
              <div className="financial-breakdown-card">
                <div className="fin-row">
                  <div className="fin-label-block">
                    <h4>Routine Maintenance Reserve</h4>
                    <p>Ordinary upkeep, seasonal detailing, preventative cleaning, and minor touchups.</p>
                  </div>
                  <div className="fin-value" data-testid="bp-val-routine">{usd(result.routineMonthly)}/mo</div>
                </div>
                <div className="fin-row">
                  <div className="fin-label-block">
                    <h4>Replacement Reserve</h4>
                    <p>Sinking fund dedicated strictly to major systems eventually reaching end-of-life.</p>
                  </div>
                  <div className="fin-value" data-testid="bp-val-replacement">{usd(result.replacementMonthly)}/mo</div>
                </div>
                <div className="fin-row total-row">
                  <div className="fin-label-block">
                    <h4>Recommended Total Home Reserve</h4>
                    <p>Combined sustainable monthly savings target to cover both operating tiers cleanly.</p>
                  </div>
                  <div className="fin-value" data-testid="bp-val-total">{usd(result.totalMonthly)}/mo</div>
                </div>
              </div>

              {/* Exposure banner */}
              {result.hasExposure && (
                <div
                  className={`exposure-banner-card ${result.isHighRisk ? "high-risk" : ""}`}
                  id="exposure-wrapper-card"
                  data-testid="bp-exposure"
                >
                  <h4>Potential Upcoming Major-Repair Costs</h4>
                  <div className="exposure-large-sum" data-testid="bp-exposure-range">
                    {usd(result.exposureMin)} – {usd(result.exposureMax)}
                  </div>
                  <p>
                    Based on the age of your home's major systems and the property characteristics you
                    entered. Your current reserve recommendation is designed to help prepare for these
                    potential expenses over time. It does not represent a contractor quote or guarantee
                    that these repairs will be required.
                  </p>
                </div>
              )}

              {/* Narrative */}
              <div className="narrative-box">
                <p data-testid="bp-narrative">{result.narrative}</p>
              </div>

              {/* Accordion */}
              <div className="accordion-box">
                <div
                  className="accordion-header"
                  onClick={() => setAccordionOpen((o) => !o)}
                  data-testid="bp-accordion-header"
                >
                  <span>How your reserve was calculated</span>
                  <span data-testid="bp-accordion-arrow">{accordionOpen ? "▲" : "▼"}</span>
                </div>
                {accordionOpen && (
                  <div className="accordion-content" data-testid="bp-accordion-content">
                    <ul>
                      {result.factors.map((f, i) => (
                        <li key={i}>{f}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Priority systems */}
              <div className="section-title">Highest Priority Systems</div>
              <div id="priorities-wrapper" data-testid="bp-priorities">
                {result.priorities.map((p, i) => (
                  <div className={`priority-item ${p.cls}`} key={i}>
                    <div className="status-dot">{p.dot}</div>
                    <div className="priority-info">
                      <h5>{p.title}</h5>
                      <p>{p.msg}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Upgrade / lead box */}
              <div className="upgrade-box" id="lead-module">
                <h4>📋 Want a Printable Copy of Your Plan?</h4>
                <p>
                  Your results above are yours to keep. Want a clean, printable version to save
                  or share? Add your info and we'll generate your Personalized Home Maintenance Plan.
                  It features:
                </p>
                <ul className="benefit-list">
                  <li>Recommended monthly reserve</li>
                  <li>Category-by-category allocations</li>
                  <li>Component priority ranking</li>
                  <li>Upcoming replacement considerations</li>
                  <li>Suggested seasonal check schedules</li>
                  <li>Printable &amp; saveable format version</li>
                </ul>

                {leadDone ? (
                  <div className="success-message" id="lead-success" data-testid="bp-lead-success">
                    ✨ Your printable Personalized Home Maintenance Plan is ready — a print window
                    just opened so you can save it as a PDF or print it. We've also shared your
                    details with the Heavenly team, and we'll reach out to help you put the plan
                    into action.
                  </div>
                ) : (
                  <form className="lead-form" onSubmit={handleLeadSubmit} data-testid="bp-lead-form">
                    <input
                      type="text"
                      placeholder="Your name"
                      value={lead.name}
                      onChange={(e) => setLead((s) => ({ ...s, name: e.target.value }))}
                      data-testid="bp-lead-name"
                    />
                    <input
                      type="tel"
                      placeholder="Your phone number"
                      value={lead.phone}
                      onChange={(e) => setLead((s) => ({ ...s, phone: e.target.value }))}
                      data-testid="bp-lead-phone"
                    />
                    <input
                      type="email"
                      placeholder="Your email"
                      value={lead.email}
                      onChange={(e) => setLead((s) => ({ ...s, email: e.target.value }))}
                      data-testid="bp-lead-email"
                    />
                    <input
                      type="text"
                      placeholder="Property address or area (optional)"
                      value={lead.area}
                      onChange={(e) => setLead((s) => ({ ...s, area: e.target.value }))}
                      data-testid="bp-lead-area"
                    />
                    <button
                      type="submit"
                      className="submit-lead-btn"
                      disabled={leadSubmitting}
                      data-testid="bp-lead-submit"
                    >
                      {leadSubmitting ? "Preparing your plan…" : "Get My Printable Maintenance Plan"}
                    </button>
                    <p style={{ fontSize: "11px", color: "#64748b", textAlign: "center", margin: "4px 0 0" }}>
                      For a personalized service quote, we'll review the specific job(s) you need done.
                    </p>
                  </form>
                )}
              </div>

              {/* Service CTA */}
              <div className="service-cta-box">
                <h4>🛠️ Need Help Prioritizing These Projects?</h4>
                <p>
                  From deep seasonal cleaning to handyman honey-do lists and preventative upkeep,{" "}
                  <span className="service-brand">Heavenly Home &amp; Office</span> makes managing your
                  home effortless.
                </p>
                <div className="service-list">Cleaning • Handyman • Painting • Home Maintenance</div>
                <button type="button" className="service-cta-btn" onClick={scrollToQuote} data-testid="bp-schedule-btn">
                  Schedule Your Home Maintenance Assessment
                </button>
              </div>

              <p className="disclaimer-text">
                Planning estimates only. Actual maintenance and replacement costs vary by location,
                materials, system type, labor rates, property condition, access, and market conditions.
                This tool does not constitute a contractor quote, inspection, warranty, or guarantee that
                a repair or replacement will be required.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
